/**
 * Shopping List Storage Management
 * Handles both API (database) and localStorage for shopping list items
 * Uses API when user is authenticated, falls back to localStorage otherwise
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { convertToPurchaseQuantity } from "@/utils/ingredientConverter";
import { categorizeIngredient } from "@/utils/ingredientCategories";
import { getCurrentUser } from "@/utils/auth";
const STORAGE_KEY = "noAdsCooking_shoppingList";
const SYNC_KEY = "noAdsCooking_shoppingList_synced";
// Get WordPress REST API base URL
function getApiUrl() {
    const wpData = window.wpData;
    if (wpData === null || wpData === void 0 ? void 0 : wpData.apiUrl) {
        return wpData.apiUrl.replace('wp/v2/', 'noads/v1/');
    }
    return '/wp-json/noads/v1/';
}
// Get WordPress nonce for CSRF protection
function getNonce() {
    const wpData = window.wpData;
    return (wpData === null || wpData === void 0 ? void 0 : wpData.nonce) || '';
}
// Helper to make authenticated API requests
function apiRequest(endpoint_1) {
    return __awaiter(this, arguments, void 0, function* (endpoint, options = {}) {
        const url = getApiUrl() + endpoint;
        const nonce = getNonce();
        const defaultOptions = Object.assign({ credentials: 'include', headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (nonce && { 'X-WP-Nonce': nonce })), options.headers) }, options);
        return fetch(url, defaultOptions);
    });
}
/**
 * Get all shopping list items from localStorage
 */
function getShoppingListLocal() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored)
            return [];
        return JSON.parse(stored);
    }
    catch (error) {
        console.error("Error loading shopping list from localStorage:", error);
        return [];
    }
}
/**
 * Get all shopping list items from API
 */
function getShoppingListApi() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield apiRequest('user/shopping-list');
            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }
            const data = yield response.json();
            return Array.isArray(data) ? data : [];
        }
        catch (error) {
            console.error("Error loading shopping list from API:", error);
            throw error;
        }
    });
}
export function getShoppingList() {
    // If called without await, return sync version (localStorage)
    const user = getCurrentUser();
    if (!user) {
        return getShoppingListLocal();
    }
    // Return promise for async API call
    return getShoppingListApi().then((apiItems) => {
        // If API returns empty array, check localStorage as fallback
        if (apiItems.length === 0) {
            const localItems = getShoppingListLocal();
            // If localStorage has items, use them (they may not be synced yet)
            if (localItems.length > 0) {
                console.warn("API returned empty array, using localStorage items");
                return localItems;
            }
        }
        return apiItems;
    }).catch(() => {
        // Fallback to localStorage on error
        return getShoppingListLocal();
    });
}
/**
 * Get shopping list synchronously (localStorage only)
 * Use this when you need immediate access without async
 */
export function getShoppingListSync() {
    return getShoppingListLocal();
}
/**
 * Save shopping list to localStorage
 */
function saveShoppingListLocal(items) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        // Dispatch event for header badge updates
        window.dispatchEvent(new Event("shoppingListChanged"));
    }
    catch (error) {
        console.error("Error saving shopping list to localStorage:", error);
    }
}
/**
 * Save shopping list to API
 */
function saveShoppingListApi(items) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield apiRequest('user/shopping-list', {
                method: 'POST',
                body: JSON.stringify({ items }),
            });
            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }
            // Dispatch event for header badge updates
            window.dispatchEvent(new Event("shoppingListChanged"));
            return true;
        }
        catch (error) {
            console.error("Error saving shopping list to API:", error);
            throw error;
        }
    });
}
/**
 * Save shopping list (tries API first, falls back to localStorage)
 */
function saveShoppingList(items) {
    return __awaiter(this, void 0, void 0, function* () {
        const user = yield getCurrentUser();
        if (user) {
            try {
                // Try API first
                yield saveShoppingListApi(items);
                // Also save to localStorage as backup
                saveShoppingListLocal(items);
                return;
            }
            catch (error) {
                // Fallback to localStorage on API error
                console.warn("API save failed, using localStorage:", error);
            }
        }
        // Use localStorage
        saveShoppingListLocal(items);
    });
}
/**
 * Add ingredients from a recipe to shopping list
 * Returns count of items added
 */
export function addIngredientsToList(ingredients, recipeId, recipeName, servings) {
    return __awaiter(this, void 0, void 0, function* () {
        const currentList = yield getShoppingList();
        const newItems = [];
        ingredients.forEach((ingredient) => {
            // Handle both RecipeIngredient (ingredient) and DetailedRecipeIngredient (item)
            // Check for truthy values, not just property existence
            const ingredientName = ingredient.ingredient || ingredient.item;
            // Skip invalid ingredients
            if (!ingredient || !ingredientName) {
                console.warn("Skipping invalid ingredient:", ingredient);
                return;
            }
            // Convert to purchase quantity
            const amount = typeof ingredient.amount === 'string'
                ? parseFloat(ingredient.amount) || 1
                : ingredient.amount || 1;
            const converted = convertToPurchaseQuantity(amount, ingredient.unit || "", ingredientName);
            // Create item ID based on ingredient details
            const itemId = `${recipeId}-${ingredient.id || ingredientName}`;
            // Check if this exact item already exists
            const existingIndex = currentList.findIndex(item => item.itemName.toLowerCase() === converted.item.toLowerCase() &&
                item.recipeId === recipeId);
            if (existingIndex !== -1) {
                // Item exists - aggregate quantities
                const existing = currentList[existingIndex];
                const existingQty = parseFloat(existing.purchaseQuantity) || 1;
                const newQty = parseFloat(converted.quantity) || 1;
                currentList[existingIndex] = Object.assign(Object.assign({}, existing), { purchaseQuantity: (existingQty + newQty).toString(), duplicateCount: (existing.duplicateCount || 1) + 1 });
            }
            else {
                // New item
                const category = categorizeIngredient(ingredientName);
                newItems.push({
                    id: itemId,
                    originalIngredient: ingredient,
                    purchaseQuantity: converted.quantity,
                    purchaseUnit: converted.unit,
                    itemName: converted.item,
                    category,
                    checked: false,
                    recipeId,
                    recipeName,
                    servings,
                    addedAt: new Date().toISOString()
                });
            }
        });
        const updatedList = [...currentList, ...newItems];
        yield saveShoppingList(updatedList);
        return newItems.length;
    });
}
/**
 * Check if a recipe's ingredients are already in the list
 */
export function isRecipeInList(recipeId) {
    return __awaiter(this, void 0, void 0, function* () {
        const list = yield getShoppingList();
        return list.some(item => item.recipeId === recipeId);
    });
}
/**
 * Get count of items from a specific recipe
 */
export function getRecipeItemCount(recipeId) {
    return __awaiter(this, void 0, void 0, function* () {
        const list = yield getShoppingList();
        return list.filter(item => item.recipeId === recipeId).length;
    });
}
/**
 * Toggle item checked state
 */
export function toggleItemChecked(itemId) {
    return __awaiter(this, void 0, void 0, function* () {
        const user = yield getCurrentUser();
        if (user) {
            try {
                const list = yield getShoppingList();
                const item = list.find(i => i.id === itemId);
                if (item) {
                    const response = yield apiRequest(`user/shopping-list/${itemId}`, {
                        method: 'PATCH',
                        body: JSON.stringify({ checked: !item.checked }),
                    });
                    if (response.ok) {
                        // Update local cache
                        item.checked = !item.checked;
                        saveShoppingListLocal(list);
                        window.dispatchEvent(new Event("shoppingListChanged"));
                        return;
                    }
                }
            }
            catch (error) {
                console.warn("API toggle failed, using localStorage:", error);
            }
        }
        // Fallback to localStorage
        const list = getShoppingListLocal();
        const index = list.findIndex(item => item.id === itemId);
        if (index !== -1) {
            list[index].checked = !list[index].checked;
            saveShoppingListLocal(list);
        }
    });
}
/**
 * Clear all items from shopping list
 */
export function clearShoppingList() {
    return __awaiter(this, void 0, void 0, function* () {
        const user = yield getCurrentUser();
        if (user) {
            try {
                const response = yield apiRequest('user/shopping-list/clear', {
                    method: 'DELETE',
                });
                if (response.ok) {
                    saveShoppingListLocal([]);
                    window.dispatchEvent(new Event("shoppingListChanged"));
                    return;
                }
            }
            catch (error) {
                console.warn("API clear failed, using localStorage:", error);
            }
        }
        // Fallback to localStorage
        saveShoppingListLocal([]);
        window.dispatchEvent(new Event("shoppingListChanged"));
    });
}
/**
 * Clear only checked items
 */
export function clearCheckedItems() {
    return __awaiter(this, void 0, void 0, function* () {
        const user = yield getCurrentUser();
        if (user) {
            try {
                const response = yield apiRequest('user/shopping-list/clear-checked', {
                    method: 'DELETE',
                });
                if (response.ok) {
                    const list = yield getShoppingList();
                    const unchecked = list.filter(item => !item.checked);
                    saveShoppingListLocal(unchecked);
                    window.dispatchEvent(new Event("shoppingListChanged"));
                    return;
                }
            }
            catch (error) {
                console.warn("API clear checked failed, using localStorage:", error);
            }
        }
        // Fallback to localStorage
        const list = getShoppingListLocal();
        const unchecked = list.filter(item => !item.checked);
        saveShoppingListLocal(unchecked);
        window.dispatchEvent(new Event("shoppingListChanged"));
    });
}
/**
 * Get items grouped by category
 */
export function getGroupedByCategory() {
    return __awaiter(this, void 0, void 0, function* () {
        const list = yield getShoppingList();
        const grouped = {};
        list.forEach(item => {
            if (!grouped[item.category]) {
                grouped[item.category] = [];
            }
            grouped[item.category].push(item);
        });
        return grouped;
    });
}
/**
 * Get items grouped by recipe
 */
export function getGroupedByRecipe() {
    return __awaiter(this, void 0, void 0, function* () {
        const list = yield getShoppingList();
        const grouped = {};
        list.forEach(item => {
            if (!grouped[item.recipeName]) {
                grouped[item.recipeName] = [];
            }
            grouped[item.recipeName].push(item);
        });
        return grouped;
    });
}
/**
 * Aggregate duplicate ingredients across recipes
 * e.g., "2 cups flour" from Recipe A + "1 cup flour" from Recipe B = "3 cups flour"
 */
export function aggregateIngredients(items) {
    const aggregated = new Map();
    items.forEach(item => {
        const key = item.itemName.toLowerCase();
        if (aggregated.has(key)) {
            const existing = aggregated.get(key);
            const existingQty = parseFloat(existing.purchaseQuantity) || 0;
            const newQty = parseFloat(item.purchaseQuantity) || 0;
            // Only aggregate if units match
            if (existing.purchaseUnit === item.purchaseUnit) {
                aggregated.set(key, Object.assign(Object.assign({}, existing), { purchaseQuantity: (existingQty + newQty).toString(), recipeName: `${existing.recipeName} + ${item.recipeName}`, checked: existing.checked && item.checked // Checked only if both are checked
                 }));
            }
            else {
                // Different units - keep separate
                aggregated.set(`${key}-${item.id}`, item);
            }
        }
        else {
            aggregated.set(key, item);
        }
    });
    return Array.from(aggregated.values());
}
/**
 * Get total item count (sync version for immediate access)
 */
export function getItemCount() {
    return getShoppingListLocal().length;
}
/**
 * Get unchecked item count (sync version)
 */
export function getUncheckedItemCount() {
    const list = getShoppingListLocal();
    return list.filter(item => !item.checked).length;
}
/**
 * Check if all items are checked (sync version)
 */
export function areAllItemsChecked() {
    const list = getShoppingListLocal();
    if (list.length === 0)
        return false;
    return list.every(item => item.checked);
}
/**
 * Format shopping list for sharing (plain text)
 */
export function formatForSharing() {
    return __awaiter(this, arguments, void 0, function* (groupBy = "category") {
        const list = yield getShoppingList();
        if (list.length === 0)
            return "Your shopping list is empty.";
        const grouped = groupBy === "category"
            ? yield getGroupedByCategory()
            : yield getGroupedByRecipe();
        let text = "🛒 Shopping List\n\n";
        Object.entries(grouped).forEach(([groupName, items]) => {
            text += `${groupName}\n`;
            items.forEach(item => {
                const check = item.checked ? "✓" : "○";
                const duplicate = item.duplicateCount && item.duplicateCount > 1
                    ? ` ×${item.duplicateCount}`
                    : "";
                text += `  ${check} ${item.purchaseQuantity} ${item.purchaseUnit} ${item.itemName}${duplicate}\n`;
            });
            text += "\n";
        });
        return text;
    });
}
/**
 * Format for delivery service (just ingredient names, comma-separated)
 */
export function formatForDeliveryService() {
    return __awaiter(this, void 0, void 0, function* () {
        const list = yield getShoppingList();
        if (list.length === 0)
            return "";
        return list
            .filter(item => !item.checked)
            .map(item => {
            const duplicate = item.duplicateCount && item.duplicateCount > 1
                ? ` (${item.duplicateCount}x)`
                : "";
            return `${item.purchaseQuantity} ${item.purchaseUnit} ${item.itemName}${duplicate}`;
        })
            .join(", ");
    });
}
/**
 * Sync localStorage data to database (called on login)
 */
export function syncLocalStorageToApi() {
    return __awaiter(this, void 0, void 0, function* () {
        const user = yield getCurrentUser();
        if (!user) {
            return;
        }
        // Check if already synced
        const synced = localStorage.getItem(SYNC_KEY);
        if (synced === 'true') {
            return;
        }
        const localItems = getShoppingListLocal();
        if (localItems.length === 0) {
            localStorage.setItem(SYNC_KEY, 'true');
            return;
        }
        try {
            // Get existing items from API
            const apiItems = yield getShoppingListApi().catch(() => []);
            // Merge: prefer API items, add local items that don't exist in API
            const apiItemIds = new Set(apiItems.map(item => item.id));
            const itemsToAdd = localItems.filter(item => !apiItemIds.has(item.id));
            if (itemsToAdd.length > 0) {
                yield apiRequest('user/shopping-list', {
                    method: 'POST',
                    body: JSON.stringify({ items: itemsToAdd }),
                });
            }
            // Mark as synced
            localStorage.setItem(SYNC_KEY, 'true');
            // Update local storage with merged data
            const mergedItems = [...apiItems, ...itemsToAdd];
            saveShoppingListLocal(mergedItems);
            console.log(`Synced ${itemsToAdd.length} items from localStorage to database`);
        }
        catch (error) {
            console.error("Error syncing localStorage to API:", error);
        }
    });
}
